// This is a poor man's "Either", without any monadic capabilities.
import { assertNever } from './errors';

/**
 * @deprecated use `@deltasierra/type-utilities -> SuccessResult<...>` instead
 */
export interface Success<TValue> {
    success: true;
    value: TValue;
}

/**
 * @deprecated use `@deltasierra/type-utilities -> FailureResult<...>` instead
 */
export interface Failure<TFailure> {
    success: false;
    failure: TFailure;
}

/**
 * @deprecated use `@deltasierra/type-utilities -> Result<...>` instead
 */
export type Result<TFailure, TValue> = Failure<TFailure> | Success<TValue>;

/**
 * @deprecated use `@deltasierra/type-utilities -> Promise<Result<...>>` instead
 *
 * Convenience alias
 */
export type PResult<TFailure, TValue> = Promise<Result<TFailure, TValue>>;

/**
 * @deprecated use `@deltasierra/type-utilities -> successResult()` instead
 * @param value -
 * @returns A success result
 */
export function resultSuccess<T>(value: T): Success<T> {
    return {
        success: true,
        value,
    };
}

/**
 * @deprecated use `@deltasierra/type-utilities -> failureResult()` instead
 * @param failure -
 * @returns A failure result
 */
export function resultFailure<T>(failure: T): Failure<T> {
    return {
        failure,
        success: false,
    };
}

/**
 * @deprecated use `@deltasierra/type-utilities -> isFailureResult()` instead
 * @param result -
 * @returns boolean
 */
export function isFailure<TFailure, TValue>(result: Result<TFailure, TValue>): result is Failure<TFailure> {
    return !result.success;
}

/**
 * @deprecated Explicitly handle the results instead and use `@deltasierra/type-utilities -> Result<...>` instead
 * @param result -
 * @param onSuccess -
 * @param onFailure -
 * @returns cata
 */
export function cataResult<TFailure, TValue, TReturn>(
    result: Result<TFailure, TValue>,
    onSuccess: (value: TValue) => TReturn,
    onFailure: (failure: TFailure) => TReturn,
): TReturn {
    switch (result.success) {
        case true:
            return onSuccess(result.value);
        case false:
            return onFailure(result.failure);
        default:
            throw assertNever(result);
    }
}

/**
 * @deprecated Explicitly handle the results instead and use `@deltasierra/type-utilities -> Result<...>` instead
 * @param onSuccess -
 * @param onFailure -
 * @returns callback
 */
export function wrapCataResult<TFailure, TValue, TReturn>(
    onSuccess: (value: TValue) => TReturn,
    onFailure: (failure: TFailure) => TReturn,
): (result: Result<TFailure, TValue>) => TReturn {
    return (result: Result<TFailure, TValue>) => cataResult(result, onSuccess, onFailure);
}

/**
 * @deprecated Explicitly handle the results instead and use `@deltasierra/type-utilities -> Result<...>` instead
 * @param presult -
 * @param onSuccess -
 * @param onFailure -
 * @returns cata
 */
export async function cataResultP<TFailure, TValue, TReturn>(
    presult: PResult<TFailure, TValue>,
    onSuccess: (value: TValue) => TReturn,
    onFailure: (failure: TFailure) => TReturn,
): Promise<TReturn> {
    return cataResult(await presult, onSuccess, onFailure);
}

/**
 * @deprecated Use `@deltasierra/type-utilities -> unwrapOrThrowResult()` instead
 * @param result -
 * @returns result
 */
export function unwrapOrThrowResult<TFailure extends Error, TValue>(result: Result<TFailure, TValue>): TValue {
    switch (result.success) {
        case true:
            return result.value;
        case false:
            throw result.failure;
        default:
            throw assertNever(result);
    }
}

/**
 * @deprecated Explicitly handle the results instead and use `@deltasierra/type-utilities -> Result<...>` instead
 * @returns function
 */
export function wrapUnwrapOrThrowResult<TFailure extends Error, TValue>(): (
    result: Result<TFailure, TValue>,
) => TValue {
    return (result: Result<TFailure, TValue>) => unwrapOrThrowResult(result);
}

/**
 * @deprecated Explicitly map results instead and use `@deltasierra/type-utilities -> Result<...>` instead
 * @param result -
 * @param onFailure -
 * @returns result
 */
export function mapFailure<TOldFailure, TValue, TNewFailure>(
    result: Result<TOldFailure, TValue>,
    onFailure: (failure: TOldFailure) => Result<TNewFailure, TValue>,
): Result<TNewFailure, TValue> {
    switch (result.success) {
        case true:
            return result;
        case false:
            return onFailure(result.failure);
        default:
            throw assertNever(result);
    }
}

/**
 * @deprecated Explicitly map results instead and use `@deltasierra/type-utilities -> Result<...>` instead
 * @param result -
 * @param onSuccess -
 * @returns result
 */
export function mapSuccess<TFailure, TOldValue, TNewValue>(
    result: Result<TFailure, TOldValue>,
    onSuccess: (value: TOldValue) => Result<TFailure, TNewValue>,
): Result<TFailure, TNewValue> {
    switch (result.success) {
        case true:
            return onSuccess(result.value);
        case false:
            return result;
        default:
            throw assertNever(result);
    }
}

/**
 * @deprecated Explicitly map results instead and use `@deltasierra/type-utilities -> Result<...>` instead
 * @param onSuccess -
 * @returns result callback
 */
export function wrapMapSuccess<TFailure, TOldValue, TNewValue>(
    onSuccess: (value: TOldValue) => Result<TFailure, TNewValue>,
): (result: Result<TFailure, TOldValue>) => Result<TFailure, TNewValue> {
    return (result: Result<TFailure, TOldValue>) => mapSuccess(result, onSuccess);
}

/**
 * @deprecated Explicitly map results instead and use `@deltasierra/type-utilities -> Result<...>` instead
 * @param resultP -
 * @param onSuccess -
 * @returns result promise
 */
export async function mapSuccessP<TFailure, TOldValue, TNewValue>(
    resultP: Promise<Result<TFailure, TOldValue>>,
    onSuccess: (value: TOldValue) => Promise<Result<TFailure, TNewValue>> | Result<TFailure, TNewValue>,
): Promise<Result<TFailure, TNewValue>> {
    return resultP.then(result => mapSuccess(result, onSuccess as (value: TOldValue) => Result<TFailure, TNewValue>));
}

/**
 * @deprecated Explicitly filter results instead and use `@deltasierra/type-utilities -> Result<...>` instead
 * Converts an array of results to a result, with the failures or values as arrays.
 * @param results - a list of results
 * @returns a list of failures (if any exist) or values (if no failures exist)
 */
export function sequenceResults<TFailure, TValue>(
    results: Array<Result<TFailure, TValue>>,
): Result<TFailure[], TValue[]> {
    const values = [];
    const failures = [];
    for (const result of results) {
        if (isFailure(result)) {
            failures.push(result.failure);
        } else if (failures.length === 0) {
            values.push(result.value);
        }
    }
    if (failures.length === 0) {
        return resultSuccess(values);
    } else {
        return resultFailure(failures);
    }
}

/**
 * @deprecated Explicitly filter results instead or make a function in
 * `@deltasierra/type-utilities -> Result<...>` instead
 *
 * Gathers up all successes from a list of results, filtering out any failures
 * @param results - a list of results
 * @returns a list of all successes
 */
export function gatherSuccesses<TFailure, TValue>(results: Array<Result<TFailure, TValue>>): TValue[] {
    return (results.filter(result => !isFailure(result)) as Array<Success<TValue>>).map(({ value }) => value);
}
